<script setup>
import Select from 'primevue/select'
import Spinner from '@/components/shared/core/Spinner.vue'
import CloseButton from '@/components/shared/buttons/CloseButton.vue'
import {toRefs} from 'vue'

//Emits and props
const emit = defineEmits([
  'input',
  'dirty',
  'search',
])
const props = defineProps({
  value: {
    type: [String, Number, Object, Array, Boolean],
    default: '',
  },
  options: {
    type: Array,
    default: () => [],
  },
  labelProp: {
    type: String,
    default: 'label',
  },
  valueProp: {
    type: String,
    default: 'value',
  },
  isClearable: {
    type: Boolean,
    default: false,
  },
  isFilterable: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
})

//Get refs
const {options, valueProp} = toRefs(props)

//Get option value
const getOptionValue = option => {
  if (option && typeof option === 'object') {
    return option[valueProp.value]
  }
  return option ?? null
}

//Input handling
const onInput = event => {

  //Get value and option
  const value = getOptionValue(event)
  const option = options.value.find(option => getOptionValue(option) === value)

  //Emit
  emit('input', {value, option})
  emit('dirty', {value, option})
}
</script>

<template>
  <Select
    v-bind="$attrs"
    dir="ltr"
    size="large"
    :model-value="value"
    :options="options"
    :option-label="labelProp"
    :option-value="valueProp"
    :show-clear="isClearable && !isDisabled && !!value"
    :filter="isFilterable"
    :disabled="isDisabled"
    :loading="isLoading"
    @update:model-value="onInput($event)"
    @search="$emit('search', $event)"
  >
    <template #option="{option}">
      <div
        :class="$style.option"
        :data-value="option[valueProp]"
      >
        <slot
          name="option"
          v-bind="option"
        >
          <span :class="$style.optionLabel">{{ option[labelProp] }}</span>
        </slot>
      </div>
    </template>

    <template #loadingicon>
      <Spinner />
    </template>

    <template #clearicon="{clearCallback}">
      <CloseButton
        aria-label="Clear selection"
        class="p-select-clear-icon"
        @click="clearCallback()"
      />
    </template>
  </Select>
</template>

<style module>
.option {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  overflow: hidden;
}
.optionLabel {
  display: block;
  overflow: hidden;
  max-width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
